/**
 * # Back-to-listing scroll restoration
 *
 * When a user opens a listing card (news, items, …) and then navigates back, we
 * want them to land on the *exact* listing they came from — same pagination
 * page, same collection path — scrolled to where they were.
 *
 * In an ASwap (semi-SPA) world the browser's own history already returns to the
 * exact previous URL on `history.back()` (pagination params included; a card
 * reached from `/relacje` returns to `/relacje`, not its canonical parent). The
 * only thing ASwap breaks is scroll position: it forces `scrollTo(0,0)` on every
 * swap, including back navigations. So this module does just two things:
 *
 *   1. **Departure** — on a click into a card (`a[data-list-item]`), record the
 *      listing's scroll position onto the *current* history entry via
 *      `replaceState`. No cookie, no server storage → no consent gate; it is
 *      per-session navigation state that never leaves the browser.
 *   2. **Return** — after a back navigation re-renders the listing, restore the
 *      saved scroll position and move focus to the originating card, then clear
 *      the saved state (one-shot).
 *
 * The visible back control (`a[data-list-back]`) prefers a real `history.back()`
 * when there is an in-session entry to return to, falling back to its own `href`
 * (the canonical parent listing) for direct / no-referrer entries.
 */

const SELECTOR_LIST_ITEM = 'a[data-list-item]';
const SELECTOR_BACK = 'a[data-list-back]';
const STATE_KEY = 'rtmListing';

interface ListingState {
  url: string;
  scrollY: number;
  itemId: string;
}

/**
 * Number of same-session, same-origin forward navigations we can safely
 * `history.back()` through. Incremented on ASwap-eligible forward link clicks,
 * decremented on `popstate`. Resets to 0 on a full reload — correct, since a
 * freshly loaded page has no in-session back history of its own.
 */
let internalDepth = 0;

const currentUrl = (): string => location.pathname + location.search;

const getLenis = (): { scrollTo: (t: number, o?: object) => void; resize: () => void } | undefined =>
  (window as any).lenis;

const escapeAttr = (value: string): string =>
  (window.CSS && CSS.escape) ? CSS.escape(value) : value.replace(/"/g, '\\"');

/**
 * Record the listing scroll position on the current history entry before ASwap
 * pushes the card's entry. When the user returns to this entry the state is
 * still attached, so we can restore it.
 */
const saveListingState = (itemId: string): void => {
  const state: ListingState = { url: currentUrl(), scrollY: window.scrollY, itemId };
  history.replaceState({ ...(history.state || {}), [STATE_KEY]: state }, '');
};

/** Drop the saved listing state from the current entry (one-shot consume). */
const clearListingState = (): void => {
  if (!history.state || !history.state[STATE_KEY]) {
    return;
  }
  const { [STATE_KEY]: _drop, ...rest } = history.state;
  history.replaceState(rest, '');
};

const isInternalForward = (link: HTMLAnchorElement): boolean => {
  if (link.target === '_blank' || link.classList.contains('no-as')) {
    return false;
  }
  const href = link.getAttribute('href');
  if (!href || href.startsWith('#')) {
    return false;
  }
  try {
    return new URL(link.href).origin === location.origin;
  } catch {
    return false;
  }
};

const onBackClick = (e: MouseEvent): void => {
  // Prefer real history when there is an in-session entry to return to — it
  // lands on the exact listing the user came from. Stop propagation so ASwap's
  // own click handler (which doesn't check defaultPrevented) doesn't also
  // navigate to the href. With no in-session history, fall through untouched
  // and let ASwap navigate to the href (the canonical parent listing).
  if (internalDepth > 0) {
    e.preventDefault();
    e.stopPropagation();
    history.back();
  }
};

const onDocumentClick = (e: MouseEvent): void => {
  // Let the browser handle modified / non-primary clicks (new tab, etc.).
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  const link = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a');
  if (!link) {
    return;
  }
  if (link.matches(SELECTOR_BACK)) {
    onBackClick(e);
    return;
  }
  if (link.matches(SELECTOR_LIST_ITEM) && link.dataset.listItem) {
    saveListingState(link.dataset.listItem);
  }
  if (isInternalForward(link)) {
    internalDepth++;
  }
};

const onPopState = (): void => {
  if (internalDepth > 0) {
    internalDepth--;
  }
};

/**
 * Restore the saved scroll position for the listing entry we just returned to,
 * and move focus to the originating card. Called after each ASwap swap and once
 * on init (to cover a back navigation that arrived via a full page load).
 *
 * ASwap defers its own `scrollTo(0,0)` ~5ms past `onViewSwapComplete`, so the
 * actual scroll is applied on a later frame to land last. Guarded by a URL match
 * so it never fires on a fresh forward visit to the same listing.
 */
export const restoreListingScroll = (): void => {
  const state = history.state?.[STATE_KEY] as ListingState | undefined;
  if (!state || state.url !== currentUrl()) {
    return;
  }
  // Consume immediately so a re-entrant swap can't apply it twice.
  clearListingState();
  const apply = (): void => {
    const lenis = getLenis();
    if (lenis) {
      // Lenis caches the scroll limit (scrollHeight − clientHeight) and only
      // recomputes on its own ResizeObserver tick, which hasn't fired yet this
      // soon after the swap. Without this the target is clamped to the *outgoing*
      // (often shorter) page's limit and the restore lands short. Force a resize
      // so the limit reflects the freshly-swapped listing before we scroll.
      lenis.resize();
      lenis.scrollTo(state.scrollY, { immediate: true, force: true });
    } else {
      window.scrollTo(0, state.scrollY);
    }
    // a11y: return keyboard focus to the card the user came from, without
    // re-scrolling away from the restored position.
    const card = document.querySelector<HTMLElement>(
      `[data-list-item="${escapeAttr(state.itemId)}"]`
    );
    card?.focus({ preventScroll: true });
  };
  requestAnimationFrame(() => requestAnimationFrame(apply));
};

export const initListings = (): void => {
  document.documentElement.addEventListener('click', onDocumentClick);
  window.addEventListener('popstate', onPopState);
  // Cover a back navigation that landed here via a full page load.
  restoreListingScroll();
};
